import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { IMAGES } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const ITEMS = [
    { img: "/assets/wallet-1.jpeg", title: "Onyx Stripe Bifold", cat: "Men's Wallets", tag: "Workshop Original" },
    { img: IMAGES.laptopMen, title: "Meridian Laptop Brief", cat: "Leather Laptop Bags", tag: "Collection" },
    { img: "/assets/candle-1.png", title: "Sunflower Harvest Bouquet", cat: "Handmade Candles", tag: "Workshop Original" },
    { img: "/assets/wallet-2.jpeg", title: "Saddle Stitch Bifold", cat: "Men's Wallets", tag: "Workshop Original" },
    { img: "/assets/candle-3.png", title: "Rose Bloom Jar", cat: "Handmade Candles", tag: "Workshop Original" },
    { img: IMAGES.laptopWomen, title: "Meridian Tote — Women's Line", cat: "Leather Laptop Bags", tag: "Collection" },
    { img: "/assets/wallet-4.jpeg", title: "Onyx Tab Bifold", cat: "Men's Wallets", tag: "Workshop Original" },
    { img: "/assets/candle-2.png", title: "Daisy Meadow Platter", cat: "Handmade Candles", tag: "Workshop Original" },
    { img: IMAGES.sling, title: "Voyager Crossbody Sling", cat: "Leather Sling Bags", tag: "Collection" },
    { img: "/assets/bag-duffle.png", title: "The Overlander Duffle", cat: "Travel & Duffle Bags", tag: "Workshop Original" },
    { img: "/assets/bag-sling.png", title: "Voyager Sling — Color Range", cat: "Leather Sling Bags", tag: "Workshop Original" },
    { img: "/assets/bag-waist.png", title: "Voyager Waist Pouch Range", cat: "Leather Sling Bags", tag: "Workshop Original" },
    { img: "/assets/bag-backpack-green.png", title: "Emerald Trail Backpack", cat: "Travel & Duffle Bags", tag: "Workshop Original" },
    { img: "/assets/bag-backpack-tan.png", title: "Summit Tan Rucksack", cat: "Travel & Duffle Bags", tag: "Workshop Original" },
    { img: "/assets/bag-messenger.png", title: "Scout Vertical Messenger", cat: "Leather Sling Bags", tag: "Workshop Original" },
    { img: "/assets/bag-hide-duffle.png", title: "Hair-On Hide Weekender", cat: "Travel & Duffle Bags", tag: "Workshop Original" },
    { img: "/assets/bag-backpack-green-back.png", title: "Emerald Trail — Utility Back", cat: "Travel & Duffle Bags", tag: "Workshop Original" },
    { img: "/assets/wallet-3.jpeg", title: "Saddle Stitch — Reverse", cat: "Men's Wallets", tag: "Workshop Original" },
    { img: "/assets/candle-4.png", title: "Floating Petal Bowl", cat: "Handmade Candles", tag: "Workshop Original" },
    { img: "/assets/magnet-2.png", title: "The Keepsake Display Board", cat: "Fridge Magnets", tag: "Workshop Original" },
    { img: "/assets/magnet-3.png", title: "Ruby Rose Vase Magnet", cat: "Fridge Magnets", tag: "Workshop Original" },
    { img: "/assets/candle-5.png", title: "Sunflower Crystal Jar", cat: "Handmade Candles", tag: "Workshop Original" },
    { img: "/assets/magnet-1.png", title: "Azure Blossom Vase Magnet", cat: "Fridge Magnets", tag: "Workshop Original" },
    { img: "/assets/magnet-4.png", title: "Blush Tulip Vase Magnet", cat: "Fridge Magnets", tag: "Workshop Original" },
    { img: "/assets/magnet-5.png", title: "Lilac Bloom Vase Magnet", cat: "Fridge Magnets", tag: "Workshop Original" },
];

const FILTERS = ["All", "Men's Wallets", "Leather Laptop Bags", "Leather Sling Bags", "Travel & Duffle Bags", "Handmade Candles", "Fridge Magnets"];

export const Gallery = () => {
    const [filter, setFilter] = useState("All");
    const [active, setActive] = useState(null);

    const visible = filter === "All" ? ITEMS : ITEMS.filter((i) => i.cat === filter);
    const step = (dir) => setActive((a) => (a + dir + visible.length) % visible.length);

    return (
        <section id="gallery" data-testid="gallery-section" className="relative py-24 sm:py-32 bg-[#181410] grain overflow-hidden">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
                >
                    <div>
                        <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-4">
                            The Gallery
                        </p>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#F4EFEA] tracking-tight leading-[1.05]">
                            Straight from
                            <br />
                            the <em className="italic text-[#D4A373]">workshop</em>.
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm text-[#8C8075] leading-relaxed">
                        Real pieces off our production floor alongside collection imagery —
                        every item shown can be produced to your specification, finish and label.
                    </p>
                </motion.div>

                <div className="flex flex-wrap gap-2.5 mb-10" data-testid="gallery-filters">
                    {FILTERS.map((f) => {
                        const activeChip = filter === f;
                        return (
                            <button
                                key={f}
                                data-testid={`gallery-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                onClick={() => { setFilter(f); setActive(null); }}
                                className={`px-4 py-2 rounded-sm text-[11px] tracking-[0.12em] uppercase border transition-colors duration-300 ${
                                    activeChip
                                        ? "bg-[#C58B49] border-[#C58B49] text-[#0F0C08]"
                                        : "border-[#2E2620] text-[#C2B7AD] hover:border-[#C58B49]/60 hover:text-[#D4A373]"
                                }`}
                            >
                                {f}
                            </button>
                        );
                    })}
                </div>

                <div className="columns-2 md:columns-3 gap-5 [column-fill:balance]">
                    <AnimatePresence mode="popLayout">
                        {visible.map((item, i) => (
                            <motion.button
                                key={item.img}
                                data-testid={`gallery-tile-${i}`}
                                onClick={() => setActive(i)}
                                initial={{ opacity: 0, y: 36 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: (i % 3) * 0.06, ease: EASE }}
                                className="group relative w-full mb-5 break-inside-avoid overflow-hidden rounded-md border border-[#2E2620] hover:border-[#C58B49]/50 transition-colors duration-500 text-left"
                            >
                                <img
                                    src={item.img}
                                    alt={`${item.title} — ${item.cat} by FnB International`}
                                    loading="lazy"
                                    className="w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0C08]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="absolute inset-x-0 bottom-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                                    <span className="font-mono2 text-[9px] tracking-[0.25em] uppercase text-[#D4A373]">
                                        {item.tag}
                                    </span>
                                    <h3 className="font-display text-xl font-semibold text-[#F4EFEA] leading-tight mt-1">
                                        {item.title}
                                    </h3>
                                    <p className="text-[11px] tracking-[0.15em] uppercase text-[#8C8075] mt-0.5">{item.cat}</p>
                                </div>
                                {item.tag === "Workshop Original" && (
                                    <span className="absolute top-4 left-4 px-2.5 py-1 rounded-sm bg-[#0F0C08]/70 backdrop-blur border border-[#C58B49]/40 font-mono2 text-[9px] tracking-[0.2em] uppercase text-[#D4A373]">
                                        FnB Made
                                    </span>
                                )}
                            </motion.button>
                        ))}
                    </AnimatePresence>
                </div>
            </div>

            <AnimatePresence>
                {active !== null && visible[active] && (
                    <motion.div
                        data-testid="gallery-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[70] bg-[#0F0C08]/92 backdrop-blur-md flex items-center justify-center p-5"
                        onClick={() => setActive(null)}
                    >
                        <button
                            data-testid="gallery-close-btn"
                            onClick={() => setActive(null)}
                            className="absolute top-6 right-6 w-11 h-11 rounded-full border border-[#4A3E34] text-[#E2D4C9] flex items-center justify-center hover:border-[#C58B49] hover:text-[#D4A373] transition-colors"
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>
                        <button
                            data-testid="gallery-prev-btn"
                            onClick={(e) => { e.stopPropagation(); step(-1); }}
                            className="absolute left-4 sm:left-8 w-11 h-11 rounded-full border border-[#4A3E34] text-[#E2D4C9] flex items-center justify-center hover:border-[#C58B49] hover:text-[#D4A373] transition-colors"
                            aria-label="Previous"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            data-testid="gallery-next-btn"
                            onClick={(e) => { e.stopPropagation(); step(1); }}
                            className="absolute right-4 sm:right-8 w-11 h-11 rounded-full border border-[#4A3E34] text-[#E2D4C9] flex items-center justify-center hover:border-[#C58B49] hover:text-[#D4A373] transition-colors"
                            aria-label="Next"
                        >
                            <ChevronRight size={18} />
                        </button>
                        <motion.figure
                            key={visible[active].img}
                            initial={{ opacity: 0, scale: 0.96, y: 16 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.45, ease: EASE }}
                            className="max-w-3xl w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={visible[active].img}
                                alt={visible[active].title}
                                className="w-full max-h-[74vh] object-contain rounded-md border border-[#2E2620]"
                                data-testid="gallery-lightbox-image"
                            />
                            <figcaption className="mt-4 flex items-baseline justify-between gap-4">
                                <span className="font-display text-2xl font-semibold text-[#F4EFEA]">
                                    {visible[active].title}
                                </span>
                                <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#C58B49] shrink-0">
                                    {visible[active].tag} · {visible[active].cat}
                                </span>
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
